"use client"

import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { useI18n } from '@/context/i18n'

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const { lang } = useI18n()

  const [preferences, setPreferences] = useState({
    essential: true, // always true
    analytics: false,
    marketing: false,
  })

  useEffect(() => {
    // Check local storage for consent
    const consentStr = localStorage.getItem('cookie-consent')
    if (!consentStr) {
      // Delay slightly for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500)
      return () => clearTimeout(timer)
    } else {
      try {
        const consent = JSON.parse(consentStr)
        setPreferences(consent)
      } catch(e) {
        // Fallback for old format
      }
    }
  }, [])

  const saveConsent = (prefs: any) => {
    localStorage.setItem('cookie-consent', JSON.stringify(prefs))
    // Notifica a scripts de terceros (p. ej. Meta Pixel) del cambio de consentimiento
    window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: prefs }))
    setIsVisible(false)
    setShowSettings(false)
  }

  const handleAcceptAll = () => {
    saveConsent({ essential: true, analytics: true, marketing: true })
  }

  const handleDeclineAll = () => {
    saveConsent({ essential: true, analytics: false, marketing: false })
  }

  const handleSaveSettings = () => {
    saveConsent(preferences)
  }

  const handleToggle = (key: keyof typeof preferences) => {
    if (key === 'essential') return
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }))
  }

  if (!isVisible && !showSettings) return null

  return (
    <>
      {isVisible && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-gray-200 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] animate-in slide-in-from-bottom duration-500">
          <div className="max-w-7xl mx-auto px-4 py-5 md:py-4 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
            <div className="flex-1 text-sm text-gray-600 text-center md:text-left">
              <p className="mb-1 text-[15px] font-medium text-gray-800">
                {lang === 'EN' ? 'We value your privacy' : 'Valoramos tu privacidad'}
              </p>
              <p className="mb-2 md:mb-0">
                {lang === 'EN' 
                  ? 'We use cookies and similar technologies to improve your experience on our site, analyze performance and traffic, and personalize content.' 
                  : 'Utilizamos cookies y tecnologías similares para mejorar tu experiencia en nuestro sitio, analizar el rendimiento y tráfico, y personalizar el contenido.'}
              </p>
              <p className="text-[13px] text-gray-500 mt-1">
                {lang === 'EN' ? 'By clicking "Accept All", you consent to the use of all cookies. ' : 'Al hacer clic en "Aceptar todas", aceptas el uso de todas las cookies. '}
                <Link href="/aviso-de-privacidad" className="text-[#FB0009] hover:underline font-semibold">
                  {lang === 'EN' ? 'Read our Privacy Policy' : 'Lee nuestro Aviso de Privacidad'}
                </Link>
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
              <button 
                type="button"
                onClick={() => {
                  console.log('Configurar clicked');
                  setShowSettings(true);
                }}
                className="px-6 py-3 md:py-2.5 text-sm font-semibold text-gray-600 bg-transparent border border-gray-300 hover:bg-gray-50 rounded-xl transition-colors whitespace-nowrap w-full sm:w-auto"
              >
                {lang === 'EN' ? 'Configure' : 'Configurar'}
              </button>
              <button 
                type="button"
                onClick={handleDeclineAll}
                className="px-6 py-3 md:py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors whitespace-nowrap w-full sm:w-auto"
              >
                {lang === 'EN' ? 'Decline Non-Essential' : 'Rechazar no esenciales'}
              </button>
              <button 
                type="button"
                onClick={handleAcceptAll}
                className="px-6 py-3 md:py-2.5 text-sm font-semibold text-white bg-[#FB0009] hover:bg-[#d60007] shadow-md hover:shadow-lg hover:-translate-y-0.5 rounded-xl transition-all whitespace-nowrap w-full sm:w-auto"
              >
                {lang === 'EN' ? 'Accept All' : 'Aceptar todas'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="fixed inset-0 z-[99999] bg-black/60 flex items-center justify-center p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">
                {lang === 'EN' ? 'Cookie Preferences' : 'Preferencias de Cookies'}
              </h2>
              <p className="text-sm text-gray-500 mt-2">
                {lang === 'EN' 
                  ? 'Manage your cookie preferences. Essential cookies are necessary for the site to function and cannot be disabled.' 
                  : 'Gestiona tus preferencias de cookies. Las cookies esenciales son necesarias para que el sitio funcione y no se pueden desactivar.'}
              </p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Essential */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">{lang === 'EN' ? 'Essential Cookies' : 'Cookies Esenciales'}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {lang === 'EN' ? 'Required for the website to function properly. Includes security and basic navigation.' : 'Necesarias para que el sitio web funcione correctamente. Incluyen seguridad y navegación básica.'}
                  </p>
                </div>
                <div className="relative inline-flex items-center cursor-not-allowed">
                  <div className="w-11 h-6 bg-gray-300 rounded-full peer"></div>
                  <div className="absolute left-[2px] top-[2px] bg-white w-5 h-5 rounded-full transition-transform translate-x-full"></div>
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">{lang === 'EN' ? 'Analytics Cookies' : 'Cookies Analíticas'}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {lang === 'EN' ? 'Help us understand how visitors interact with our website to improve the experience.' : 'Nos ayudan a comprender cómo interactúan los visitantes con nuestro sitio web para mejorar la experiencia.'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input type="checkbox" className="sr-only peer" checked={preferences.analytics} onChange={() => handleToggle('analytics')} />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FB0009]"></div>
                </label>
              </div>

              {/* Marketing */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">{lang === 'EN' ? 'Marketing Cookies' : 'Cookies de Marketing'}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {lang === 'EN' ? 'Used to deliver relevant advertisements and track campaign performance.' : 'Utilizadas para mostrar anuncios relevantes y hacer un seguimiento del rendimiento de las campañas.'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input type="checkbox" className="sr-only peer" checked={preferences.marketing} onChange={() => handleToggle('marketing')} />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FB0009]"></div>
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
              <button 
                type="button"
                onClick={handleAcceptAll}
                className="px-5 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-300 hover:bg-gray-100 rounded-xl transition-colors"
              >
                {lang === 'EN' ? 'Accept All' : 'Aceptar todas'}
              </button>
              <button 
                type="button"
                onClick={handleSaveSettings}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-[#FB0009] hover:bg-[#d60007] shadow-sm hover:shadow-md rounded-xl transition-all"
              >
                {lang === 'EN' ? 'Save Preferences' : 'Guardar preferencias'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
